import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client'; 
import OrderForm from './OrderForm';
import OrderHistory from './OrderHistory';
import MarketSelector from './MarketSelector';
import TradingChart from './TradingChart'; // Import TradingChart
import './Trading.css';

function Trading({ marketData, selectedPair, setSelectedPair }) {
  const [userOrders, setUserOrders] = useState([]);
  const [userTrades, setUserTrades] = useState([]);
  const [wallet, setWallet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMarketList, setShowMarketList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPairData, setCurrentPairData] = useState(null);
  const [allPairs, setAllPairs] = useState([]);
  const [pairsLoading, setPairsLoading] = useState(true);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [realTimePrices, setRealTimePrices] = useState({});
  const [orderBookData, setOrderBookData] = useState({ bids: [], asks: [] });
  const [activeSection, setActiveSection] = useState('trading');
  const [hasOpenOrder, setHasOpenOrder] = useState(false);
  const [openOrderInfo, setOpenOrderInfo] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [refreshing, setRefreshing] = useState({
    wallet: false,
    orders: false,
    history: false
  });

  const binanceWebSocketRef = useRef(null);
  const orderBookWebSocketRef = useRef(null);
  const userDataWebSocketRef = useRef(null);
  const marketListRef = useRef(null);
  const pairSelectorRef = useRef(null);

  // Auto-refresh intervals (in seconds)
  const refreshIntervals = {
    wallet: 30,    // 30 seconds
    orders: 5,    // 15 seconds  
    history: 60    // 30 seconds
  };

  // Use refs for data that shouldn't trigger re-renders
  const stableMarketData = useRef(marketData);
  const stableAllPairs = useRef([]);

  // Device detection - memoized to prevent unnecessary re-renders
  const deviceType = useMemo(() => {
    const width = windowSize.width;
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024
    };
  }, [windowSize.width]);

  // Get current price from real-time data - optimized with refs
  const currentPrice = useMemo(() => {
    if (selectedPair && realTimePrices[selectedPair]) {
      return realTimePrices[selectedPair].price;
    }
    if (selectedPair && stableMarketData.current[selectedPair]) {
      return stableMarketData.current[selectedPair]?.current_price || 0;
    }
    return 50000;
  }, [selectedPair, realTimePrices]);

  // Check for open orders for the current pair
  const checkOpenOrders = useCallback(() => {
    if (!selectedPair || !userOrders.length) {
      setHasOpenOrder(false);
      setOpenOrderInfo(null);
      return;
    }

    const openOrders = userOrders.filter(order => {
      const orderPair = order.pair || order.symbol;
      const status = order.status || order.orderStatus;
      return orderPair === selectedPair && 
             ['open', 'pending', 'partial'].includes(status);
    });

    if (openOrders.length > 0) {
      setHasOpenOrder(true);
      setOpenOrderInfo(openOrders[0]); // Show info about the first open order
    } else {
      setHasOpenOrder(false);
      setOpenOrderInfo(null);
    }
  }, [selectedPair, userOrders]);

  // Check for open orders whenever selected pair or user orders change
  useEffect(() => {
    checkOpenOrders();
  }, [checkOpenOrders]);

  // Initialize WebSocket for market data
  useEffect(() => {
    if (!selectedPair) return;

    const binanceSymbol = selectedPair.replace('/', '').toLowerCase();
    const tradeWsUrl = `wss://stream.binance.com:9443/ws/${binanceSymbol}@trade`;
    const depthWsUrl = `wss://stream.binance.com:9443/ws/${binanceSymbol}@depth5@100ms`;
    
    // Clean up existing connections
    if (binanceWebSocketRef.current) {
      binanceWebSocketRef.current.close();
    }
    if (orderBookWebSocketRef.current) {
      orderBookWebSocketRef.current.close();
    }

    setConnectionStatus('connecting');

    // Trade WebSocket
    const tradeWs = new WebSocket(tradeWsUrl);
    binanceWebSocketRef.current = tradeWs;

    tradeWs.onopen = () => {
      console.log('Market WebSocket connected');
      setConnectionStatus('connected');
    };

    tradeWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.e === 'trade') {
          const price = parseFloat(data.p);
          setRealTimePrices(prev => ({
            ...prev,
            [selectedPair]: {
              price,
              quantity: parseFloat(data.q),
              isBuyerMaker: data.m,
              timestamp: Date.now(),
              direction: prev[selectedPair] ? 
                (price > prev[selectedPair].price ? 'up' : 'down') : 'up'
            }
          }));
        }
      } catch (error) {
        console.error('Error parsing trade WebSocket message:', error);
      }
    };

    // Order Book WebSocket
    const depthWs = new WebSocket(depthWsUrl);
    orderBookWebSocketRef.current = depthWs;

    depthWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.e === 'depthUpdate') {
          const bids = data.b.map(bid => ({
            price: parseFloat(bid[0]),
            amount: parseFloat(bid[1])
          })).sort((a, b) => b.price - a.price).slice(0, 12);

          const asks = data.a.map(ask => ({
            price: parseFloat(ask[0]),
            amount: parseFloat(ask[1])
          })).sort((a, b) => a.price - b.price).slice(0, 12);

          setOrderBookData({ bids, asks });
        }
      } catch (error) {
        console.error('Error parsing depth WebSocket message:', error);
      }
    };

    const handleError = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus('error');
    };

    const handleClose = () => {
      console.log('Market WebSocket disconnected');
      setConnectionStatus('disconnected');
      setTimeout(() => {
        if (selectedPair) {
          setConnectionStatus('connecting');
        }
      }, 3000);
    };

    tradeWs.onerror = handleError;
    tradeWs.onclose = handleClose;
    depthWs.onerror = handleError;
    depthWs.onclose = handleClose;

    return () => {
      if (tradeWs.readyState === WebSocket.OPEN) tradeWs.close();
      if (depthWs.readyState === WebSocket.OPEN) depthWs.close();
    };
  }, [selectedPair]);

  // User Data WebSocket for real-time updates - FIXED VERSION
  // In Trading.js - Replace the user data WebSocket useEffect
// In Trading.js - ENHANCED WebSocket listeners for admin approvals
useEffect(() => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user'));
  
  if (!token || !user) {
    console.log('No user token found, skipping WebSocket connection');
    return;
  }

  console.log('🔗 Setting up enhanced Socket.io connection for admin approvals...');

  const socket = io('https://trcsite.net', {
    transports: ['websocket', 'polling']
  });

  userDataWebSocketRef.current = socket;

  socket.on('connect', () => {
    console.log('✅ Socket.io connected for real-time admin updates');
    setConnectionStatus('connected');
    
    socket.emit('authenticate', { 
      token: token 
    });
    
    // Join user room for admin broadcasts
    socket.emit('userAuthenticated', user.id);
  });

  socket.on('authenticated', (data) => {
    console.log('✅ Socket.io authentication successful:', data);
    setConnectionStatus('connected');
  });

  // 🔥 ENHANCED REAL-TIME LISTENERS FOR ADMIN APPROVALS

  // 1. Listen for order status changes from admin
  socket.on('orderStatusChanged', (data) => {
    console.log('🔄 Order status changed by admin:', data);
    setLastUpdate(Date.now());
    
    setUserOrders(prevOrders => {
      const orderIndex = prevOrders.findIndex(order => 
        order.id === data.id || order._id === data.id
      );
      
      if (orderIndex !== -1) {
        const updatedOrders = [...prevOrders];
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          ...data,
          status: data.status,
          filled_amount: data.filled_amount || updatedOrders[orderIndex].filled_amount
        };
        console.log('✅ Order updated in Trading component:', data.id, data.status);
        return updatedOrders;
      } else {
        console.log('❌ Order not found in local state:', data.id);
        return prevOrders;
      }
    });
    
    // Refresh orders list to ensure consistency
    setTimeout(() => {
      fetchUserData();
    }, 300);
  });

  // 2. Listen for order filled events from admin approvals
  socket.on('order_filled', (data) => {
    console.log('✅ Order filled event from admin:', data);
    setLastUpdate(Date.now());
    
    setUserOrders(prevOrders => 
      prevOrders.filter(order => 
        order.id !== data.data.id && order._id !== data.data.id
      )
    );
    
    setUserTrades(prevTrades => {
      const newTrade = {
        ...data.data,
        id: data.data.id || data.data._id
      };
      return [newTrade, ...prevTrades].slice(0, 100);
    });
    
    // Force refresh all data
    setTimeout(() => {
      fetchUserData();
    }, 500);
  });

  // 3. Listen for global order updates
  socket.on('globalOrderUpdate', (data) => {
    console.log('🌍 Global order update received:', data);
    setLastUpdate(Date.now());
    
    if (data.status === 'filled' || data.status === 'cancelled') {
      setUserOrders(prevOrders => 
        prevOrders.filter(order => 
          order.id !== data.id && order._id !== data.id
        )
      );
    } else {
      setUserOrders(prevOrders => {
        const orderIndex = prevOrders.findIndex(order => 
          order.id === data.id || order._id === data.id
        );
        
        if (orderIndex !== -1) {
          const updatedOrders = [...prevOrders];
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            ...data
          };
          return updatedOrders;
        }
        return prevOrders;
      });
    }
  });

  // 4. Listen for trade updates from admin approvals
  socket.on('trade_update', (data) => {
    console.log('💸 Trade update from admin approval:', data);
    setLastUpdate(Date.now());
    
    setUserTrades(prevTrades => {
      const newTrade = {
        ...data.data,
        id: data.data.id || data.data._id
      };
      return [newTrade, ...prevTrades].slice(0, 100);
    });
  });

  // 5. Listen for wallet updates after admin approvals
  socket.on('wallet_update', (data) => {
    console.log('💰 Wallet update after admin approval:', data);
    setLastUpdate(Date.now());
    
    if (data.data && Array.isArray(data.data)) {
      setWallet(prevWallet => {
        const updatedWallet = [...prevWallet];
        data.data.forEach(update => {
          const index = updatedWallet.findIndex(item => 
            item.currency === update.currency
          );
          if (index !== -1) {
            updatedWallet[index] = {
              ...updatedWallet[index],
              balance: parseFloat(update.balance) || 0,
              available: parseFloat(update.available) || 0,
              locked: parseFloat(update.locked) || 0
            };
          } else {
            updatedWallet.push({
              currency: update.currency,
              balance: parseFloat(update.balance) || 0,
              available: parseFloat(update.available) || 0,
              locked: parseFloat(update.locked) || 0
            });
          }
        });
        return updatedWallet;
      });
    }
  });

  // 6. Enhanced order update listener
  socket.on('order_update', (data) => {
    console.log('📝 Order update received:', data);
    setLastUpdate(Date.now());
    
    // Handle both data formats: data.data and direct data
    const orderData = data.data || data;
    
    setUserOrders(prevOrders => {
      const orderIndex = prevOrders.findIndex(order => 
        order.id === orderData.id || order._id === orderData.id
      );
      
      if (orderIndex !== -1) {
        const updatedOrders = [...prevOrders];
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          ...orderData,
          status: orderData.status,
          filled_amount: orderData.filled_amount || updatedOrders[orderIndex].filled_amount
        };
        return updatedOrders;
      } else {
        // Add new order if not found
        return [orderData, ...prevOrders];
      }
    });
  });

  // 7. Listen for broadcast order filled events
  socket.on('broadcastOrderFilled', (data) => {
    console.log('📢 Broadcast order filled:', data);
    setLastUpdate(Date.now());
    
    setUserOrders(prevOrders => 
      prevOrders.filter(order => 
        order.id !== data.id && order._id !== data.id
      )
    );
    
    // Refresh all data to ensure consistency
    setTimeout(() => {
      fetchUserData();
    }, 300);
  });

  // Existing listeners (keep these)
  socket.on('user_data_snapshot', (data) => {
    console.log('📊 Full user data snapshot received:', data);
    setLastUpdate(Date.now());
    
    if (data.data) {
      setUserOrders(data.data.open_orders || []);
      setUserTrades(data.data.recent_trades || []);
      setWallet(data.data.wallets || []);
    }
  });

  socket.on('disconnect', () => {
    console.log('🔌 Socket.io disconnected');
    setConnectionStatus('disconnected');
  });

  socket.on('error', (error) => {
    console.error('❌ Socket.io error:', error);
    setConnectionStatus('error');
  });

  return () => {
    if (socket) {
      socket.disconnect();
    }
  };
}, []);

  // Window size tracking - debounced
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Update refs when props change (without causing re-renders)
  useEffect(() => {
    stableMarketData.current = marketData;
  }, [marketData]);

  useEffect(() => {
    stableAllPairs.current = allPairs;
  }, [allPairs]);

  // Prevent body scroll when market list is open
  useEffect(() => {
    if (showMarketList) {
      document.body.classList.add('market-list-open');
    } else {
      document.body.classList.remove('market-list-open');
    }
  }, [showMarketList]);

  // Theme management
  useEffect(() => {
    document.body.className = isDarkTheme ? 'dark-theme' : 'light-theme';
  }, [isDarkTheme]);

  // Auto-refresh functionality
  useEffect(() => {
    const intervals = {};

    // Auto-refresh wallet every 30 seconds
    intervals.wallet = setInterval(() => {
      if (activeSection === 'trading') {
        refreshWallet();
      }
    }, refreshIntervals.wallet * 1000);

    // Auto-refresh orders every 15 seconds when in orders section
    intervals.orders = setInterval(() => {
      if (activeSection === 'orders') {
        refreshOrders();
      }
    }, refreshIntervals.orders * 1000);

    // Auto-refresh history every 30 seconds when in history section
    intervals.history = setInterval(() => {
      if (activeSection === 'history') {
        refreshHistory();
      }
    }, refreshIntervals.history * 1000);

    return () => {
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [activeSection]);

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  // Fetch trading pairs
  useEffect(() => {
    const fetchAllPairs = async () => {
      try {
        setPairsLoading(true);
        const response = await axios.get('https://trcsite.net/api/market/pairs');
        if (response.data && response.data.pairs) {
          const pairs = response.data.pairs.map(pair => pair.symbol);
          setAllPairs(pairs);
          stableAllPairs.current = pairs;
        } else {
          throw new Error('Invalid response format');
        }
        setPairsLoading(false);
      } catch (error) {
        console.error('Error fetching trading pairs:', error);
        const fallbackPairs = [
          'BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'SOL/USDT', 'XRP/USDT',
          'ADA/USDT', 'AVAX/USDT', 'DOT/USDT', 'DOGE/USDT', 'MATIC/USDT'
        ];
        setAllPairs(fallbackPairs);
        stableAllPairs.current = fallbackPairs;
        setPairsLoading(false);
      }
    };

    fetchAllPairs();
  }, []);

  // Set default pair
  useEffect(() => {
    if (allPairs.length > 0 && !selectedPair) {
      setSelectedPair('BTC/USDT');
    }
  }, [allPairs, selectedPair, setSelectedPair]);

  // Refresh functions for each section
  const refreshWallet = useCallback(async () => {
    try {
      setRefreshing(prev => ({ ...prev, wallet: true }));
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('https://trcsite.net/api/wallet', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const walletData = response.data.wallets || response.data || [];
      const formattedWallet = Array.isArray(walletData) 
        ? walletData.map(item => ({
            currency: item.currency || item.symbol || 'Unknown',
            balance: parseFloat(item.balance) || 0,
            available: parseFloat(item.available) || parseFloat(item.balance) || 0,
            locked: parseFloat(item.locked) || 0
          }))
        : [];

      setWallet(formattedWallet);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Error refreshing wallet:', error);
    } finally {
      setRefreshing(prev => ({ ...prev, wallet: false }));
    }
  }, []);

  const refreshOrders = useCallback(async () => {
    try {
      setRefreshing(prev => ({ ...prev, orders: true }));
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('https://trcsite.net/api/trade/orders', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUserOrders(response.data.orders || []);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Error refreshing orders:', error);
    } finally {
      setRefreshing(prev => ({ ...prev, orders: false }));
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    try {
      setRefreshing(prev => ({ ...prev, history: true }));
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('https://trcsite.net/api/trade/trades/history', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUserTrades(response.data.trades || []);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Error refreshing history:', error);
    } finally {
      setRefreshing(prev => ({ ...prev, history: false }));
    }
  }, []);

  // Manual refresh handlers
  const handleRefreshWallet = () => {
    refreshWallet();
  };

  const handleRefreshOrders = () => {
    refreshOrders();
  };

  const handleRefreshHistory = () => {
    refreshHistory();
  };

  // Fetch user data - INITIAL LOAD ONLY
  // In Trading.js - Enhance the fetchUserData function
const fetchUserData = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    console.log('🔍 Fetching initial user data with holdings...');
    const [ordersResponse, tradesResponse, walletResponse, holdingsResponse] = await Promise.all([
      axios.get('https://trcsite.net/api/trade/orders', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { orders: [] } })),
      axios.get('https://trcsite.net/api/trade/trades/history', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { trades: [] } })),
      axios.get('https://trcsite.net/api/wallet', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { wallets: [] } })),
      // 🔥 NEW: Fetch holdings data
      axios.get('https://trcsite.net/api/trade/holdings', {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { holdings: [] } }))
    ]);

    console.log('📥 Initial data loaded:', {
      orders: ordersResponse.data.orders?.length || 0,
      trades: tradesResponse.data.trades?.length || 0,
      wallet: walletResponse.data.wallets?.length || 0,
      holdings: holdingsResponse.data.holdings?.length || 0
    });

    setUserOrders(ordersResponse.data.orders || []);
    setUserTrades(tradesResponse.data.trades || []);
    
    const walletData = walletResponse.data.wallets || walletResponse.data || [];
    const formattedWallet = Array.isArray(walletData) 
      ? walletData.map(item => ({
          currency: item.currency || item.symbol || 'Unknown',
          balance: parseFloat(item.balance) || 0,
          available: parseFloat(item.available) || parseFloat(item.balance) || 0,
          locked: parseFloat(item.locked) || 0
        }))
      : [];
    
    setWallet(formattedWallet);
    
    // 🔥 NEW: Store holdings data if needed
    if (holdingsResponse.data.holdings) {
      // You can set this to a state if you want to display holdings in Trading component
      console.log('📊 Holdings data loaded:', holdingsResponse.data.holdings);
    }
    
    setLoading(false);
  } catch (error) {
    console.error('❌ Error fetching user data:', error);
    setUserOrders([]);
    setUserTrades([]);
    setWallet([]);
    setLoading(false);
  }
}, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Click outside handler - optimized
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMarketList && 
          marketListRef.current && 
          !marketListRef.current.contains(event.target) &&
          pairSelectorRef.current &&
          !pairSelectorRef.current.contains(event.target)) {
        setShowMarketList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMarketList]);

  // Market categories
  const marketCategories = ['All', 'USDT', 'BTC', 'ETH', 'BNB'];
  const [activeCategory, setActiveCategory] = useState('All');

  // Filter pairs - memoized with stable data
  const filteredPairs = useMemo(() => {
    let filtered = stableAllPairs.current.filter(pair => {
      const matchesSearch = pair.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = activeCategory === 'All' || pair.split('/')[1] === activeCategory;
      return matchesSearch && matchesCategory;
    });

    // Use stable market data ref to avoid re-renders
    filtered.sort((a, b) => {
      const priorityPairs = ['BTC/USDT', 'ETH/USDT', 'BCH/USDT', 'EOS/USDT'];
      
      const aIndex = priorityPairs.indexOf(a);
      const bIndex = priorityPairs.indexOf(b);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      
      const volumeA = stableMarketData.current[a]?.volume_24h || 0;
      const volumeB = stableMarketData.current[b]?.volume_24h || 0;
      return volumeB - volumeA;
    });

    return filtered.slice(0, deviceType.isMobile ? 20 : 40);
  }, [searchTerm, activeCategory, deviceType.isMobile, lastUpdate]);

  // Price levels - memoized
  const priceLevels = useMemo(() => {
    if (orderBookData.bids.length > 0 || orderBookData.asks.length > 0) {
      const bids = orderBookData.bids.map((bid, i) => ({
        id: `bid-${i}`,
        price: bid.price,
        amount: bid.amount,
        total: bid.price * bid.amount,
        type: 'bid'
      }));

      const asks = orderBookData.asks.map((ask, i) => ({
        id: `ask-${i}`,
        price: ask.price,
        amount: ask.amount,
        total: ask.price * ask.amount,
        type: 'ask'
      }));

      return { bids, asks };
    }

    // Fallback data
    const basePrice = currentPrice || 50000;
    const bids = Array.from({ length: 12 }, (_, i) => {
      const price = basePrice * (1 - (i + 1) * 0.001);
      const amount = Math.random() * 2 + 0.5;
      return { id: `bid-${i}`, price, amount, total: price * amount, type: 'bid' };
    }).sort((a, b) => b.price - a.price);

    const asks = Array.from({ length: 12 }, (_, i) => {
      const price = basePrice * (1 + (i + 1) * 0.001);
      const amount = Math.random() * 2 + 0.5;
      return { id: `ask-${i}`, price, amount, total: price * amount, type: 'ask' };
    }).sort((a, b) => a.price - b.price);

    return { bids, asks };
  }, [orderBookData, currentPrice, lastUpdate]);

  // Trading functions - FIXED VERSION
  const handlePlaceOrder = async (orderData) => {
    try {
      // Check if there's already an open order for this pair
      if (hasOpenOrder) {
        alert('❌ You already have an open order for this trading pair. Please cancel the existing order or wait for it to be filled before placing a new one.');
        return;
      }

      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      
      if (!user || !token) {
        alert('Please login to place orders');
        return;
      }

      console.log('📤 Placing order:', orderData);

      // Add optimistic update for better UX
      const optimisticOrder = {
        id: `temp_${Date.now()}`,
        ...orderData,
        status: 'pending',
        timestamp: new Date().toISOString(),
        isOptimistic: true
      };
      
      setUserOrders(prev => [optimisticOrder, ...prev]);

      const response = await axios.post('https://trcsite.net/api/trade/orders/place', {
        ...orderData,
        userId: user.id
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });

      if (response.data.success) {
        console.log('✅ Order placed successfully:', response.data);
        alert(`✅ ${response.data.message}`);
        // WebSocket will update with real data shortly
      }
    } catch (error) {
      console.error('❌ Error placing order:', error);
      
      // Remove optimistic update if it exists
      setUserOrders(prev => prev.filter(order => !order.isOptimistic));
      
      alert(`❌ Error: ${error.response?.data?.error || error.message}`);
    }
  };

  // In Trading.js - Fix the handleCancelOrder function
const handleCancelOrder = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Optimistic update
      setUserOrders(prev => 
        prev.map(order => 
          order.id === orderId 
            ? { ...order, status: 'cancelling', isOptimistic: true }
            : order
        )
      );

      const response = await axios.post(`https://trcsite.net/api/trade/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      
      if (response.data.success) {
        console.log('✅ Order cancellation requested');
        // WebSocket will confirm the cancellation
      }
    } catch (error) {
      console.error('❌ Error cancelling order:', error);
      
      // Revert optimistic update
      setUserOrders(prev => 
        prev.map(order => 
          order.id === orderId && order.isOptimistic
            ? { ...order, status: 'open', isOptimistic: false }
            : order
        )
      );
      
      alert('❌ You have to wait at least 5s before cancelling an order.');
    }
  };

  // Market selection handlers
  const handlePairSelectorClick = useCallback(() => {
    setShowMarketList(prev => !prev);
  }, []);

  const handleMarketItemClick = useCallback((pair) => {
    setSelectedPair(pair);
    setShowMarketList(false);
    setSearchTerm('');
    setActiveCategory('All');
  }, [setSelectedPair]);

  const handleCloseMarketList = useCallback(() => {
    setShowMarketList(false);
  }, []);

  // Utility functions
  const formatBalance = useCallback((balance) => {
    if (balance === null || balance === undefined) return '0.00';
    const num = parseFloat(balance);
    if (isNaN(num)) return '0.00';
    if (num === 0) return '0.00';
    if (num < 0.001) return num.toFixed(6);
    if (num < 1) return num.toFixed(4);
    if (num < 1000) return num.toFixed(2);
    return num.toFixed(2);
  }, []);

  const calculateUSDValue = useCallback((walletItem) => {
    const balance = parseFloat(walletItem.balance) || 0;
    if (balance === 0) return 0;
    if (walletItem.currency === 'USDT') return balance;
    const pairSymbol = `${walletItem.currency}/USDT`;
    const price = stableMarketData.current[pairSymbol]?.current_price || currentPrice || 0;
    return balance * price;
  }, [currentPrice]);

  // Current market data
  const priceChange = currentPairData?.price_change_percentage_24h || 0.45;
  const high24h = currentPairData?.high_24h || (currentPrice * 1.02);
  const low24h = currentPairData?.low_24h || (currentPrice * 0.98);
  const volume24h = currentPairData?.volume_24h || 254678123;

  // Filter wallet
  const nonZeroWallet = useMemo(() => 
    wallet.filter(item => parseFloat(item.balance) > 0),
    [wallet, lastUpdate]
  );

  // Refresh Button Component
  const RefreshButton = ({ onRefresh, refreshing, section, interval }) => (
    <button 
      className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
      onClick={onRefresh}
      disabled={refreshing}
      title={`Auto-refreshes every ${interval} seconds. Click to refresh now.`}
    >
      <span className="refresh-icon">
        {refreshing ? '⏳' : '🔄'}
      </span>
      <span className="refresh-text">
        {refreshing ? 'Refreshing...' : 'Refresh'}
      </span>
      <span className="refresh-interval">({interval}s)</span>
    </button>
  );

  // RealTimePriceList Component - memoized to prevent re-renders
  const RealTimePriceList = useMemo(() => {
    return ({ bids, asks, pair, isMobile = false, compact = false }) => {
      const [baseCurrency, quoteCurrency] = pair ? pair.split('/') : ['', ''];
      const currentPriceData = realTimePrices[pair];
      const displayPrice = currentPriceData?.price || currentPrice;

      const formatPrice = (price) => {
        if (!price) return '0.00';
        if (price < 0.01) return price.toFixed(6);
        if (price < 1) return price.toFixed(4);
        if (price < 1000) return price.toFixed(2);
        return price.toFixed(2);
      };

      const formatAmount = (amount) => {
        if (!amount) return '0.00';
        if (amount < 0.001) return amount.toFixed(6);
        if (amount < 1) return amount.toFixed(4);
        if (amount < 1000) return amount.toFixed(2);
        return amount.toFixed(2);
      };

      return (
        <div 
          className={`realtime-price-list ${isMobile ? 'mobile' : ''}`}
          compact={compact ? "true" : undefined}
        >
          <div className="price-list-header">
            <div className="header-column price">Price ({quoteCurrency})</div>
            <div className="header-column amount">Amount ({baseCurrency})</div>
            <div className="header-column total">Total ({quoteCurrency})</div>
          </div>
          
          {/* Asks (Red) - 5 levels */}
          <div className="asks-section">
            {asks.map((ask, index) => (
              <div key={ask.id} className={`price-item ask ${index === 0 ? 'best-ask' : ''}`}>
                <div className="price-column price">
                  <span className="price-value" style={{ color: 'var(--accent-danger)' }}>
                    {formatPrice(ask.price)}
                  </span>
                  {index === 0 && <span className="best-indicator">↗</span>}
                </div>
                <div className="price-column amount">
                  <span className="amount-value">{formatAmount(ask.amount)}</span>
                </div>
                <div className="price-column total">
                  <span className="total-value">{formatPrice(ask.total)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="current-price-display">
            <div className="current-price">${formatPrice(displayPrice)}</div>
            <div className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
              {priceChange >= 0 ? '↗' : '↘'} {Math.abs(priceChange).toFixed(2)}%
            </div>
          </div>

          {/* Bids (Green) - 5 levels */}
          <div className="bids-section">
            {bids.map((bid, index) => (
              <div key={bid.id} className={`price-item bid ${index === 0 ? 'best-bid' : ''}`}>
                <div className="price-column price">
                  <span className="price-value" style={{ color: 'var(--accent-success)' }}>
                    {formatPrice(bid.price)}
                  </span>
                  {index === 0 && <span className="best-indicator">↘</span>}
                </div>
                <div className="price-column amount">
                  <span className="amount-value">{formatAmount(bid.amount)}</span>
                </div>
                <div className="price-column total">
                  <span className="total-value">{formatPrice(bid.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
  }, [realTimePrices, currentPrice, priceChange, lastUpdate]);

  // Market Selector Component
  const MarketSelectorButton = useMemo(() => {
    return () => (
      <div className="market-selector-container">
        <button 
          ref={pairSelectorRef}
          className="market-selector-btn"
          onClick={handlePairSelectorClick}
          type="button"
        >
          <span className="market-selector-icon">📈</span>
          <span className="market-selector-text">Markets</span>
        </button>
        
        {showMarketList && (
          <div className="markets-overlay">
            <div className="markets-sidebar" ref={marketListRef}>
              <div className="markets-header">
                <h3>Trading Pairs</h3>
                <button 
                  className="close-btn"
                  onClick={handleCloseMarketList}
                  type="button"
                >
                  ✕
                </button>
              </div>
              
              <div className="search-section">
                <input
                  type="text"
                  placeholder="Search trading pairs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <div className="market-categories">
                  {marketCategories.map(category => (
                    <button
                      key={category}
                      className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                      onClick={() => setActiveCategory(category)}
                      type="button"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              <div className="markets-list-container">
                <div className="markets-list">
                  {filteredPairs.length > 0 ? (
                    filteredPairs.map(pair => {
                      const isSelected = selectedPair === pair;
                      
                      return (
                        <button
                          key={pair}
                          className={`market-item ${isSelected ? 'active' : ''}`}
                          onClick={() => handleMarketItemClick(pair)}
                          type="button"
                        >
                          <div className="market-symbol">{pair}</div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="no-results">No trading pairs found</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [showMarketList, searchTerm, activeCategory, filteredPairs, selectedPair, handlePairSelectorClick, handleCloseMarketList, handleMarketItemClick]);

  // Navigation tabs
  const navigationTabs = [
    { key: 'trading', label: 'Trading', icon: '📊' },
    { key: 'orders', label: 'My Orders', icon: '📝' },
    { key: 'history', label: 'Trade History', icon: '📈' },
  ];

  // Open Order Warning Component
  const OpenOrderWarning = () => {
    if (!hasOpenOrder || !openOrderInfo) return null;

    const formatOrderInfo = (order) => {
      const type = order.type || order.orderType;
      const amount = order.amount || order.orderAmount;
      const price = order.price || order.orderPrice;
      const status = order.status || order.orderStatus;
      
      return {
        type: type?.toUpperCase(),
        amount: parseFloat(amount).toFixed(6),
        price: price ? `$${parseFloat(price).toFixed(2)}` : 'Market',
        status: status?.toUpperCase()
      };
    };

    const orderInfo = formatOrderInfo(openOrderInfo);
  };

  // Real-time Status Component
  const RealTimeStatus = () => (
    <div className="realtime-status">
      <div className={`status-indicator ${connectionStatus}`}>
        <div className="status-dot"></div>
        <span className="status-text">
          {connectionStatus === 'connected' ? 'Live Updates' : connectionStatus}
        </span>
      </div>
      {connectionStatus === 'connected' && (
        <div className="update-count">
          <span className="update-badge">Real-time</span>
        </div>
      )}
    </div>
  );

  // Render current section content
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'trading':
        return deviceType.isMobile ? (
          <div className="trading-mobile-layout">
            {/* Open Order Warning */}
            <OpenOrderWarning />
            
            {/* Main Trading Area - Grid Layout */}
            <div className="mobile-trading-grid">
              {/* Left Column: Order Book */}
              <div className="mobile-panel price-list-panel compact">
                <div className="panel-header compact">
                  <h3>Order Book</h3>
                </div>
                <RealTimePriceList 
                  bids={priceLevels.bids}
                  asks={priceLevels.asks}
                  pair={selectedPair}
                  isMobile={true}
                  compact={true}
                />
              </div>

              {/* Right Column: Order Form */}
              <div className="mobile-panel order-panel compact">
                <OrderForm 
                  pair={selectedPair}
                  currentPrice={currentPrice}
                  wallet={wallet}
                  onPlaceOrder={handlePlaceOrder}
                  isMobile={true}
                  hasOpenOrder={hasOpenOrder}
                  openOrderInfo={openOrderInfo}
                />
              </div>
            </div>

            {/* Chart Section (Always Visible at Bottom) */}
            <div className="mobile-panel chart-panel">
              <TradingChart 
                pair={selectedPair} 
                marketData={marketData}
                isMobile={true} 
              />
            </div>
          </div>
        ) : (
          <div className="trading-desktop-layout">
            {/* Open Order Warning */}
            <OpenOrderWarning />
            
            <div className="left-column">
              <div className="panel order-form-panel">
                <OrderForm 
                  pair={selectedPair}
                  currentPrice={currentPrice}
                  wallet={wallet}
                  onPlaceOrder={handlePlaceOrder}
                  hasOpenOrder={hasOpenOrder}
                  openOrderInfo={openOrderInfo}
                />
              </div>
            </div>

            <div className="center-column">
              <div className="panel price-list-panel">
                <div className="panel-header">
                  <h3>Order Book - {selectedPair}</h3>
                   
                </div>
                <RealTimePriceList 
                  bids={priceLevels.bids}
                  asks={priceLevels.asks}
                  pair={selectedPair}
                />
              </div>
            </div>

            <div className="right-column">
              <div className="panel wallet-panel">
                <div className="panel-header with-refresh">
                  <h3>Wallet Balance</h3>
                </div>
                <div className="wallet-list">
                  {nonZeroWallet.map(item => (
                    <div key={item.currency} className="wallet-item">
                      <div className="currency-info">
                        <span className="currency">{item.currency}</span>
                        <span className="balance">{formatBalance(item.balance)}</span>
                      </div>
                      <div className="usd-value">
                        ${calculateUSDValue(item).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="panel chart-panel">
                <TradingChart 
                  pair={selectedPair} 
                  marketData={marketData}
                />
              </div>
            </div>
          </div>
        );

      case 'orders':
        return (
          <div className="section-content">
            <div className="panel-header with-refresh">
              <h2>My Orders</h2>
            </div>
            <OrderHistory 
              orders={userOrders}
              onCancelOrder={handleCancelOrder}
              title="My Orders"
              showType="orders"
              compact={deviceType.isMobile}
              isMobile={deviceType.isMobile}
              realTimeUpdates={true}
              lastUpdate={lastUpdate}
            />
          </div>
        );

      case 'history':
        return (
          <div className="section-content">
            <div className="panel-header with-refresh">
              <h2>Trade History</h2>
              <RefreshButton 
                onRefresh={handleRefreshHistory}
                refreshing={refreshing.history}
                section="history"
                interval={refreshIntervals.history}
              />
            </div>
            <OrderHistory 
              orders={userTrades}
              onCancelOrder={handleCancelOrder}
              title="Trade History"
              showType="trades"
              compact={deviceType.isMobile}
              isMobile={deviceType.isMobile}
              realTimeUpdates={true}
              lastUpdate={lastUpdate}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || pairsLoading) {
    return (
      <div className="trading-loading-container">
        <div className="loading-spinner-large"></div>
        <div className="loading-title">Loading Trading Platform</div>
        <div className="loading-subtitle">WebSocket Status: {connectionStatus}</div>
      </div>
    );
  }

  return (
    <div className={`trading-platform ${deviceType.isMobile ? 'mobile' : deviceType.isTablet ? 'tablet' : 'desktop'} ${isDarkTheme ? 'dark' : 'light'}`}>
      
      {/* Mobile Header */}
      {deviceType.isMobile && (
        <header className="mobile-trading-header">
          <div className="mobile-header-top">            
            <div className="mobile-pair-selector">
              <MarketSelector 
                selectedPair={selectedPair}
                setSelectedPair={setSelectedPair}
                marketData={marketData}
                variant="mobile-hamburger"
              />
            </div>
          </div>

          {selectedPair && (
            <div className="mobile-price-display">
              <div className="mobile-price-main">
                <span className="current-price">${currentPrice.toFixed(2)}</span>
                <span className={`price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                  {priceChange >= 0 ? '↗' : '↘'}
                  {Math.abs(priceChange).toFixed(2)}%
                </span>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Desktop Header */}
      {!deviceType.isMobile && (
        <header className="trading-header">
          <div className="header-content">
            <div className="header-left">
              <MarketSelector 
                selectedPair={selectedPair}
                setSelectedPair={setSelectedPair}
                marketData={marketData}
              />
              
              {selectedPair && (
                <div className="current-pair-info">
                  <span className="pair-symbol-large">{selectedPair}</span>
                  <span className="current-price-large">${currentPrice.toFixed(2)}</span>
                  <span className={`price-change-large ${priceChange >= 0 ? 'positive' : 'negative'}`}>
                    {priceChange >= 0 ? '↗' : '↘'}
                    {Math.abs(priceChange).toFixed(2)}%
                  </span>
                </div>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="header-navigation">
              {navigationTabs.map(tab => (
                <button
                  key={tab.key}
                  className={`nav-tab ${activeSection === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveSection(tab.key)}
                >
                  <span className="tab-icon">{tab.icon}</span>
                  <span className="tab-label">{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="header-right">
               
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="trading-main">
        {renderSectionContent()}
      </main>

      {/* Mobile Bottom Navigation */}
      {deviceType.isMobile && (
        <nav className="mobile-bottom-nav">
          {navigationTabs.map(tab => (
            <button
              key={tab.key}
              className={`mobile-nav-item ${activeSection === tab.key ? 'active' : ''}`}
              onClick={() => setActiveSection(tab.key)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}

export default Trading;